import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddReelModal = ({ onClose, onAdd }) => {
  const [newReel, setNewReel] = useState({
    videoFile: null,
    videoFileObject: null,
    title: '',
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadStage, setUploadStage] = useState(''); // 'presigned', 'uploading', 'saving'

  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 100MB limit. Please choose a smaller file.');
        toast.error('File size exceeds 100MB limit. Please choose a smaller file.');
        return;
      }

      // Check file type
      if (!file.type.startsWith('video/')) {
        setError('Please select a valid video file.');
        toast.error('Please select a valid video file.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewReel({ 
          ...newReel, 
          videoFile: reader.result,
          videoFileObject: file 
        });
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!newReel.videoFileObject || !newReel.title.trim()) {
      setError('Please fill in all required fields.');
      toast.error('Please fill in all required fields.');
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL from Django
      setUploadStage('presigned');
      console.log('Step 1: Requesting presigned URL...');
      
      const presignedResponse = await axios.post(
        `${API_BASE_URL}/get-upload-url`,
        {
          filename: newReel.videoFileObject.name,
          filetype: newReel.videoFileObject.type,
          filesize: newReel.videoFileObject.size,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            // Add auth header if needed:
            // 'Authorization': `Bearer ${yourAuthToken}`
          },
        }
      );

      const { uploadUrl, fileUrl, uploadId } = presignedResponse.data;
      console.log('Presigned URL obtained:', { fileUrl, uploadId });

      // Step 2: Upload directly to DigitalOcean Spaces
      setUploadStage('uploading');
      console.log('Step 2: Uploading to DigitalOcean Spaces...');
      console.log('File size:', formatFileSize(newReel.videoFileObject.size));

      await axios.put(uploadUrl, newReel.videoFileObject, {
        headers: {
          'Content-Type': newReel.videoFileObject.type,
          'x-amz-acl': 'public-read',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
          console.log('Upload progress:', percentCompleted + '%');
        },
      });

      console.log('Upload to Spaces successful!');

      // Step 3: Save metadata to Django backend
      setUploadStage('saving');
      setUploadProgress(100);
      console.log('Step 3: Saving metadata to database...');

      const metadataResponse = await axios.post(
        `${API_BASE_URL}/save-reel-metadata`,
        {
          title: newReel.title.trim(),
          videoUrl: fileUrl,
          isActive: newReel.isActive,
          uploadId: uploadId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            // Add auth header if needed:
            // 'Authorization': `Bearer ${yourAuthToken}`
          },
        }
      );

      console.log('Metadata saved successfully:', metadataResponse.data);

      // Add the new reel to the list
      onAdd({
        id: metadataResponse.data.reel.id,
        title: metadataResponse.data.reel.title,
        video: metadataResponse.data.reel.video,
        isActive: metadataResponse.data.reel.isActive,
        createdAt: metadataResponse.data.reel.createdAt,
        updatedAt: metadataResponse.data.reel.updatedAt,
      });

      toast.success('Reel uploaded successfully! 🎉');
      onClose();

    } catch (err) {
      console.error('Upload Error Details:', {
        stage: uploadStage,
        message: err.message,
        response: err.response,
      });

      let errorMessage = 'Failed to upload reel';

      if (uploadStage === 'presigned') {
        errorMessage = err.response?.data?.error || 
                      'Failed to get upload URL. Please check your connection and try again.';
      } else if (uploadStage === 'uploading') {
        if (err.code === 'ERR_NETWORK') {
          errorMessage = 'Network error while uploading to cloud storage. Please check your internet connection.';
        } else {
          errorMessage = 'Failed to upload video to cloud storage. Please try again.';
        }
      } else if (uploadStage === 'saving') {
        errorMessage = err.response?.data?.error || 
                      'Video uploaded but failed to save metadata. Please contact support.';
      }

      setError(errorMessage);
      toast.error(errorMessage, { autoClose: 8000 });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const getUploadStageText = () => {
    switch (uploadStage) {
      case 'presigned':
        return 'Getting upload URL...';
      case 'uploading':
        return `Uploading to cloud storage... ${uploadProgress}%`;
      case 'saving':
        return 'Saving metadata...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Add New Reel</h3>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
            <p className="font-semibold mb-1">Error:</p>
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Video * (Max: 100MB)
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={isLoading}
            />
            {newReel.videoFileObject && (
              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-2">
                  File: {newReel.videoFileObject.name} ({formatFileSize(newReel.videoFileObject.size)})
                </div>
                <video 
                  src={newReel.videoFile} 
                  controls 
                  className="w-full h-40 object-cover rounded-lg" 
                />
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={newReel.title}
              onChange={(e) => setNewReel({ ...newReel, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Reel title"
              required
              disabled={isLoading}
            />
          </div>

          {/* Active Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={newReel.isActive}
              onChange={(e) => setNewReel({ ...newReel, isActive: e.target.checked })}
              id="reel-active"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="reel-active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Progress Bar */}
          {isLoading && (
            <div className="w-full">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{getUploadStageText()}</span>
                {uploadStage === 'uploading' && <span>{uploadProgress}%</span>}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    uploadStage === 'presigned' ? 'bg-yellow-500 w-1/4' :
                    uploadStage === 'uploading' ? 'bg-indigo-600' :
                    uploadStage === 'saving' ? 'bg-green-500 w-full' :
                    'bg-gray-400 w-0'
                  }`}
                  style={uploadStage === 'uploading' ? { width: `${uploadProgress}%` } : {}}
                ></div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {getUploadStageText()}
                </>
              ) : (
                'Add Reel'
              )}
            </button>
          </div>
        </form>

        {/* Upload Tips */}
        {isLoading && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              💡 <strong>Direct upload in progress...</strong> Your video is being uploaded directly to cloud storage, bypassing server limitations. Please don't close this window.
            </p>
          </div>
        )}

        {/* Benefits Info */}
        {!isLoading && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600">
              <strong>✨ Direct Upload:</strong> Videos upload directly to cloud storage (DigitalOcean Spaces), allowing files up to 100MB without server limitations!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddReelModal;